/* eslint-disable max-lines */
import { DocumentTranscriptV1 } from '@utils/types/editors/transcripts/transcript.ts';

const STORAGE_VERSION = '1.0';
const STORAGE_PREFIX = 'transcript-editor';
const MAX_HISTORY_SIZE = 20; // Reduced from 200 to save localStorage space
const CLEANUP_THRESHOLD_DAYS = 7;

export interface TranscriptStorageData {
  version: string;
  sessionId: string;
  documentId: string;
  document: DocumentTranscriptV1;
  undoStack: DocumentTranscriptV1[];
  redoStack: DocumentTranscriptV1[];
  lastLocalSave: number; // timestamp
  lastServerSync: number | null; // timestamp
  isDirty: boolean;
  filename?: string;
  timecodeOffset?: string;
}

export interface TranscriptStorageMetadata {
  sessionId: string;
  documentId: string;
  lastLocalSave: number;
  lastServerSync: number | null;
}

/**
 * Generate storage key for a transcript document
 */
function getStorageKey(sessionId: string, documentId: string): string {
  return `${STORAGE_PREFIX}-${sessionId}-${documentId}`;
}

/**
 * Generate metadata key for a transcript document
 */
function getMetadataKey(sessionId: string, documentId: string): string {
  return `${STORAGE_PREFIX}-meta-${sessionId}-${documentId}`;
}

/**
 * Check if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Save transcript document data to localStorage
 */
export function saveTranscriptToLocalStorage(
  sessionId: string,
  documentId: string,
  data: Partial<TranscriptStorageData>
): boolean {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  const key = getStorageKey(sessionId, documentId);
  const metaKey = getMetadataKey(sessionId, documentId);

  // Load existing data to merge
  const existing = loadTranscriptFromLocalStorage(sessionId, documentId);

  const defaultDocument: DocumentTranscriptV1 = {
    document: [],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document_type: 'TranscriptV1' as any,
    version: '1'
  };

  // Get undo/redo stacks - trim to max size to prevent localStorage bloat
  let undoStack = data.undoStack ?? existing?.undoStack ?? [];
  let redoStack = data.redoStack ?? existing?.redoStack ?? [];

  // Trim to max size
  if (undoStack.length > MAX_HISTORY_SIZE) {
    undoStack = undoStack.slice(-MAX_HISTORY_SIZE);
  }
  if (redoStack.length > MAX_HISTORY_SIZE) {
    redoStack = redoStack.slice(-MAX_HISTORY_SIZE);
  }

  const storageData: TranscriptStorageData = {
    version: STORAGE_VERSION,
    sessionId,
    documentId,
    document: data.document ?? existing?.document ?? defaultDocument,
    undoStack,
    redoStack,
    lastLocalSave: data.lastLocalSave ?? existing?.lastLocalSave ?? Date.now(),
    lastServerSync: data.lastServerSync ?? existing?.lastServerSync ?? null,
    isDirty: data.isDirty ?? existing?.isDirty ?? true,
    filename: data.filename ?? existing?.filename,
    timecodeOffset: data.timecodeOffset ?? existing?.timecodeOffset
  };

  // Save metadata separately for quick lookups
  const metadata: TranscriptStorageMetadata = {
    sessionId,
    documentId,
    lastLocalSave: storageData.lastLocalSave,
    lastServerSync: storageData.lastServerSync
  };

  try {
    localStorage.setItem(key, JSON.stringify(storageData));
    localStorage.setItem(metaKey, JSON.stringify(metadata));

    return true;
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      cleanupOldEntries();

      // Try one more time after cleanup, but try to preserve undo/redo if possible
      try {
        let attemptUndoStack = undoStack;
        let attemptRedoStack = redoStack;

        // Try with reduced undo/redo first (keep last 5 instead of 20)
        if (undoStack.length > 5) {
          attemptUndoStack = undoStack.slice(-5);
        }
        if (redoStack.length > 5) {
          attemptRedoStack = redoStack.slice(-5);
        }

        try {
          const storageData: TranscriptStorageData = {
            version: STORAGE_VERSION,
            sessionId,
            documentId,
            document: data.document ?? defaultDocument,
            undoStack: attemptUndoStack,
            redoStack: attemptRedoStack,
            lastLocalSave: Date.now(),
            lastServerSync: data.lastServerSync ?? null,
            isDirty: data.isDirty ?? true,
            filename: data.filename,
            timecodeOffset: data.timecodeOffset
          };
          localStorage.setItem(key, JSON.stringify(storageData));
          return true;
        } catch (stillTooLarge) {
          // If still too large, save without undo/redo but keep the document
          const minimalData: TranscriptStorageData = {
            version: STORAGE_VERSION,
            sessionId,
            documentId,
            document: data.document ?? defaultDocument,
            undoStack: [], // Empty as last resort
            redoStack: [], // Empty as last resort
            lastLocalSave: Date.now(),
            lastServerSync: data.lastServerSync ?? null,
            isDirty: data.isDirty ?? true,
            filename: data.filename,
            timecodeOffset: data.timecodeOffset
          };
          localStorage.setItem(key, JSON.stringify(minimalData));
          return true;
        }
      } catch (retryError) {
        // Last resort: try to save just the document metadata
        try {
          const metaKey = getMetadataKey(sessionId, documentId);
          const metadata: TranscriptStorageMetadata = {
            sessionId,
            documentId,
            lastLocalSave: Date.now(),
            lastServerSync: data.lastServerSync ?? null
          };
          localStorage.setItem(metaKey, JSON.stringify(metadata));
        } catch (metaError) {
          // Nothing we can do
        }
        return false;
      }
    }
    return false;
  }
}

/**
 * Load transcript document data from localStorage
 */
export function loadTranscriptFromLocalStorage(
  sessionId: string,
  documentId: string
): TranscriptStorageData | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const key = getStorageKey(sessionId, documentId);
    const data = localStorage.getItem(key);

    if (!data) {
      return null;
    }

    const parsed: TranscriptStorageData = JSON.parse(data);

    // Validate version
    if (parsed.version !== STORAGE_VERSION) {
      // Could implement migration logic here in the future
    }

    return parsed;
  } catch (error) {
    // If data is corrupted, remove it
    try {
      const key = getStorageKey(sessionId, documentId);
      localStorage.removeItem(key);
    } catch (e) {
      // Ignore cleanup errors
    }
    return null;
  }
}

/**
 * Get metadata for a transcript document
 */
export function getTranscriptMetadata(
  sessionId: string,
  documentId: string
): TranscriptStorageMetadata | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const metaKey = getMetadataKey(sessionId, documentId);
    const data = localStorage.getItem(metaKey);

    if (!data) {
      return null;
    }

    return JSON.parse(data);
  } catch (error) {
    return null;
  }
}

/**
 * Clear transcript document data from localStorage
 */
export function clearTranscriptFromLocalStorage(
  sessionId: string,
  documentId: string
): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    const key = getStorageKey(sessionId, documentId);
    const metaKey = getMetadataKey(sessionId, documentId);
    localStorage.removeItem(key);
    localStorage.removeItem(metaKey);
  } catch (error) {
    // Error clearing localStorage
  }
}

/**
 * Save undo/redo stacks to localStorage (deprecated - no longer saves to localStorage)
 * Undo/redo stacks are now kept only in memory to save localStorage space
 */
export function saveUndoRedoStacks(
  _sessionId: string,
  _documentId: string,
  _undoStack: DocumentTranscriptV1[],
  _redoStack: DocumentTranscriptV1[]
): boolean {
  // No longer save undo/redo stacks to localStorage
  return true;
}

/**
 * Mark document as synced with server
 */
export function markAsSynced(sessionId: string, documentId: string): boolean {
  return saveTranscriptToLocalStorage(sessionId, documentId, {
    lastServerSync: Date.now(),
    isDirty: false
  });
}

/**
 * Cleanup old localStorage entries
 */
export function cleanupOldEntries(): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    const now = Date.now();
    const thresholdMs = CLEANUP_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
    const keysToRemove: string[] = [];

    // Find all transcript editor keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(STORAGE_PREFIX)) {
        continue;
      }

      // Check if it's a metadata key
      if (key.startsWith(`${STORAGE_PREFIX}-meta-`)) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const metadata: TranscriptStorageMetadata = JSON.parse(data);
            const age = now - metadata.lastLocalSave;

            if (age > thresholdMs) {
              // Mark both metadata and data keys for removal
              keysToRemove.push(key);
              const dataKey = key.replace(
                `${STORAGE_PREFIX}-meta-`,
                `${STORAGE_PREFIX}-`
              );
              keysToRemove.push(dataKey);
            }
          }
        } catch (e) {
          // If we can't parse it, it's probably corrupted, so remove it
          keysToRemove.push(key);
        }
      }
    }

    // Remove old entries
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Failed to remove key
      }
    });
  } catch (error) {
    // noop
  }
}

/**
 * Get all transcript documents in localStorage
 */
export function getAllTranscriptDocuments(): TranscriptStorageMetadata[] {
  if (!isLocalStorageAvailable()) {
    return [];
  }

  const documents: TranscriptStorageMetadata[] = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${STORAGE_PREFIX}-meta-`)) {
        continue;
      }

      try {
        const data = localStorage.getItem(key);
        if (data) {
          const metadata: TranscriptStorageMetadata = JSON.parse(data);
          documents.push(metadata);
        }
      } catch (e) {
        // Skip corrupted entries
        continue;
      }
    }
  } catch (error) {
    // Error getting all transcript documents
  }

  return documents;
}

/**
 * Get storage usage estimate in bytes
 */
export function getStorageUsageEstimate(): number {
  if (!isLocalStorageAvailable()) {
    return 0;
  }

  let total = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
  } catch (error) {
    // Error calculating storage usage
  }

  return total * 2; // Each character is ~2 bytes in UTF-16
}
